/**
 * Synchronous Permission Fallback Layer
 *
 * This package provides synchronous permission evaluation for contexts
 * where async DB/API calls are not possible:
 *
 * 1. Server middleware (requirePermission.ts) — Express middleware is
 *    synchronous; it cannot await an API call mid-request pipeline.
 * 2. Client-side guards (PermissionGuard.tsx, permissions.ts) — when
 *    DB permissions have not yet loaded (source !== 'db').
 *
 * Architecture:
 *   DB permissions (core.role_permissions) → /api/auth/my-permissions
 *   → Authoritative when source === 'db'
 *   → This hardcoded map is the synchronous fallback only
 *
 * All role keys use the 10 unified roles exclusively:
 * super_admin, admin, manager, operator, finance, customer, provider,
 * user, viewer, guest
 *
 * @deprecated Prefer DB-driven permissions. This fallback will be
 * removed when requirePermission.ts migrates to DB-backed checks.
 */

export {
  PERMISSION_DOMAINS,
  PERMISSIONS,
  ADMIN_ROLES,
  USER_ROLES,
  type Permission,
  type AdminRole,
  type UserRole,
  type SystemRole,
  type RolePermissionMap,
  type PermissionDomain,
} from './types';

import {
  PERMISSIONS,
  ADMIN_ROLES,
  type Permission,
  type AdminRole,
  type RolePermissionMap,
} from './types';

/**
 * @deprecated Compile-time fallback only. DB-driven permissions via
 * fetchDynamicPermissions() are authoritative when available.
 * Uses unified role names only (no legacy roles).
 */
export const ROLE_PERMISSIONS: RolePermissionMap = {
  [ADMIN_ROLES.SUPER_ADMIN]: [
    PERMISSIONS.SYSTEM_SUPER_ADMIN,
    PERMISSIONS.SYSTEM_ADMIN,
    PERMISSIONS.DASHBOARD_ACCESS,
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.DASHBOARD_EDIT,
    PERMISSIONS.USERS_VIEW,
    PERMISSIONS.USERS_CREATE,
    PERMISSIONS.USERS_EDIT,
    PERMISSIONS.USERS_DELETE,
    PERMISSIONS.USERS_MANAGE,
    PERMISSIONS.SECURITY_VIEW,
    PERMISSIONS.SECURITY_EDIT,
    PERMISSIONS.SECURITY_MANAGE,
    PERMISSIONS.SETTINGS_VIEW,
    PERMISSIONS.SETTINGS_EDIT,
    PERMISSIONS.SETTINGS_MANAGE,
    PERMISSIONS.CONTENT_VIEW,
    PERMISSIONS.CONTENT_CREATE,
    PERMISSIONS.CONTENT_EDIT,
    PERMISSIONS.CONTENT_DELETE,
    PERMISSIONS.CONTENT_PUBLISH,
    PERMISSIONS.OPERATIONS_VIEW,
    PERMISSIONS.OPERATIONS_EDIT,
    PERMISSIONS.OPERATIONS_MANAGE,
    PERMISSIONS.FINANCE_VIEW,
    PERMISSIONS.FINANCE_EDIT,
    PERMISSIONS.FINANCE_MANAGE,
    PERMISSIONS.INTELLIGENCE_VIEW,
    PERMISSIONS.INTELLIGENCE_EDIT,
    PERMISSIONS.ANALYTICS_VIEW,
    PERMISSIONS.ANALYTICS_EDIT,
    PERMISSIONS.INTEGRATIONS_VIEW,
    PERMISSIONS.INTEGRATIONS_EDIT,
    PERMISSIONS.INTEGRATIONS_MANAGE,
    PERMISSIONS.INFRASTRUCTURE_VIEW,
    PERMISSIONS.INFRASTRUCTURE_EDIT,
    PERMISSIONS.INFRASTRUCTURE_MANAGE,
    PERMISSIONS.AUDIT_VIEW,
    PERMISSIONS.AUDIT_MANAGE,
    PERMISSIONS.PRICING_MANAGE,
    PERMISSIONS.PRICING_ANALYTICS,
  ],
  [ADMIN_ROLES.ADMIN]: [
    PERMISSIONS.SYSTEM_ADMIN,
    PERMISSIONS.DASHBOARD_ACCESS,
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.DASHBOARD_EDIT,
    PERMISSIONS.USERS_VIEW,
    PERMISSIONS.USERS_CREATE,
    PERMISSIONS.USERS_EDIT,
    PERMISSIONS.USERS_DELETE,
    PERMISSIONS.USERS_MANAGE,
    PERMISSIONS.SECURITY_VIEW,
    PERMISSIONS.SECURITY_EDIT,
    PERMISSIONS.SECURITY_MANAGE,
    PERMISSIONS.SETTINGS_VIEW,
    PERMISSIONS.SETTINGS_EDIT,
    PERMISSIONS.SETTINGS_MANAGE,
    PERMISSIONS.CONTENT_VIEW,
    PERMISSIONS.CONTENT_CREATE,
    PERMISSIONS.CONTENT_EDIT,
    PERMISSIONS.CONTENT_DELETE,
    PERMISSIONS.CONTENT_PUBLISH,
    PERMISSIONS.OPERATIONS_VIEW,
    PERMISSIONS.OPERATIONS_EDIT,
    PERMISSIONS.OPERATIONS_MANAGE,
    PERMISSIONS.FINANCE_VIEW,
    PERMISSIONS.FINANCE_EDIT,
    PERMISSIONS.FINANCE_MANAGE,
    PERMISSIONS.INTELLIGENCE_VIEW,
    PERMISSIONS.ANALYTICS_VIEW,
    PERMISSIONS.ANALYTICS_EDIT,
    PERMISSIONS.INTEGRATIONS_VIEW,
    PERMISSIONS.INTEGRATIONS_EDIT,
    PERMISSIONS.INTEGRATIONS_MANAGE,
    PERMISSIONS.INFRASTRUCTURE_VIEW,
    PERMISSIONS.INFRASTRUCTURE_EDIT,
    PERMISSIONS.INFRASTRUCTURE_MANAGE,
    PERMISSIONS.AUDIT_VIEW,
    PERMISSIONS.AUDIT_MANAGE,
    PERMISSIONS.PRICING_MANAGE,
    PERMISSIONS.PRICING_ANALYTICS,
  ],
  [ADMIN_ROLES.MANAGER]: [
    PERMISSIONS.DASHBOARD_ACCESS,
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.USERS_VIEW,
    PERMISSIONS.USERS_EDIT,
    PERMISSIONS.SECURITY_VIEW,
    PERMISSIONS.SETTINGS_VIEW,
    PERMISSIONS.CONTENT_VIEW,
    PERMISSIONS.CONTENT_EDIT,
    PERMISSIONS.OPERATIONS_VIEW,
    PERMISSIONS.OPERATIONS_EDIT,
    PERMISSIONS.OPERATIONS_MANAGE,
    PERMISSIONS.FINANCE_VIEW,
    PERMISSIONS.INTELLIGENCE_VIEW,
    PERMISSIONS.ANALYTICS_VIEW,
    PERMISSIONS.INTEGRATIONS_VIEW,
    PERMISSIONS.INFRASTRUCTURE_VIEW,
    PERMISSIONS.AUDIT_VIEW,
    PERMISSIONS.PRICING_MANAGE,
    PERMISSIONS.PRICING_ANALYTICS,
  ],
  [ADMIN_ROLES.OPERATOR]: [
    PERMISSIONS.DASHBOARD_ACCESS,
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.OPERATIONS_VIEW,
    PERMISSIONS.OPERATIONS_EDIT,
    PERMISSIONS.CONTENT_VIEW,
    PERMISSIONS.ANALYTICS_VIEW,
    PERMISSIONS.AUDIT_VIEW,
  ],
  [ADMIN_ROLES.FINANCE]: [
    PERMISSIONS.DASHBOARD_ACCESS,
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.FINANCE_VIEW,
    PERMISSIONS.FINANCE_EDIT,
    PERMISSIONS.FINANCE_MANAGE,
    PERMISSIONS.OPERATIONS_VIEW,
    PERMISSIONS.ANALYTICS_VIEW,
    PERMISSIONS.AUDIT_VIEW,
  ],
};

/** @deprecated Compile-time fallback. Use DB-driven permissions when source === 'db'. */
export function hasPermission(userRole: AdminRole | string, permission: Permission): boolean {
  const role = userRole as AdminRole;
  const rolePermissions = ROLE_PERMISSIONS[role];
  if (!rolePermissions) return false;
  if (rolePermissions.includes(PERMISSIONS.SYSTEM_SUPER_ADMIN)) return true;
  return rolePermissions.includes(permission);
}

/** @deprecated Compile-time fallback. Use DB-driven permissions when source === 'db'. */
export function hasAnyPermission(userRole: AdminRole | string, permissions: Permission[]): boolean {
  return permissions.some(permission => hasPermission(userRole, permission));
}

/** @deprecated Compile-time fallback. Use DB-driven permissions when source === 'db'. */
export function hasAllPermissions(userRole: AdminRole | string, permissions: Permission[]): boolean {
  return permissions.every(permission => hasPermission(userRole, permission));
}

/** @deprecated Compile-time fallback. Use DB-driven permissions when source === 'db'. */
export function getRolePermissions(role: AdminRole | string): Permission[] {
  return ROLE_PERMISSIONS[role as AdminRole] || [];
}

export function isValidAdminRole(role: string): role is AdminRole {
  return Object.values(ADMIN_ROLES).includes(role as AdminRole);
}

export function getAllAdminRoles(): AdminRole[] {
  return Object.values(ADMIN_ROLES);
}

export function getAllPermissions(): Permission[] {
  return Object.values(PERMISSIONS);
}
